import React, { useEffect, useRef, useCallback } from 'react';
import { Chart, registerables } from 'chart.js';
import { FaChartLine, FaChartBar, FaCalendarAlt } from 'react-icons/fa';

// Register all Chart.js components
Chart.register(...registerables);

function SalesAnalytics() {
  const lineChartRef = useRef(null);
  const barChartRef = useRef(null);
  const lineChartInstance = useRef(null);
  const barChartInstance = useRef(null);

  // Memoize chart data to prevent unnecessary recreations
  const weeklySalesData = React.useMemo(() => ({
    labels: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    datasets: [{
      label: 'Daily Sales (৳)',
      data: [12000, 19000, 15000, 22000, 18000, 25000, 21000],
      borderColor: 'rgba(220, 38, 38, 1)',
      backgroundColor: 'rgba(220, 38, 38, 0.2)',
      tension: 0.3,
    }],
  }), []);

  const popularItemsData = React.useMemo(() => ({
    labels: ['Broast Meal', 'Shawarma', 'Burger', 'Falafel', 'Juices'],
    datasets: [{
      label: 'Items Sold',
      data: [320, 180, 210, 95, 280],
      backgroundColor: 'rgba(16, 185, 129, 0.6)',
    }],
  }), []);

  // Create charts function
  const createCharts = useCallback(() => {
    // Destroy previous chart instances if they exist
    if (lineChartInstance.current) {
      lineChartInstance.current.destroy();
    }
    if (barChartInstance.current) {
      barChartInstance.current.destroy();
    }

    // Create new chart instances
    if (lineChartRef.current) {
      lineChartInstance.current = new Chart(lineChartRef.current, {
        type: 'line',
        data: weeklySalesData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback(value) {
                  return `৳${value.toLocaleString()}`;
                },
              },
            },
          },
        },
      });
    }

    if (barChartRef.current) {
      barChartInstance.current = new Chart(barChartRef.current, {
        type: 'bar',
        data: popularItemsData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
            },
          },
        },
      });
    }
  }, [weeklySalesData, popularItemsData]);

  useEffect(() => {
    createCharts();

    // Cleanup function to destroy charts when component unmounts
    return () => {
      if (lineChartInstance.current) {
        lineChartInstance.current.destroy();
        lineChartInstance.current = null;
      }
      if (barChartInstance.current) {
        barChartInstance.current.destroy();
        barChartInstance.current = null;
      }
    };
  }, [createCharts]);

  return (
    <div className="p-6 bg-gray-50">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Sales Analytics</h2>
        <div className="flex items-center space-x-2 bg-white px-3 py-2 rounded-lg border">
          <FaCalendarAlt className="text-gray-500" />
          <span className="text-sm">Last 7 Days</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-md h-96">
          <div className="flex items-center mb-4">
            <FaChartLine className="text-red-600 mr-2" />
            <h3 className="font-semibold">Sales Trend</h3>
          </div>
          <div className="h-80">
            <canvas ref={lineChartRef} />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-md h-96">
          <div className="flex items-center mb-4">
            <FaChartBar className="text-green-600 mr-2" />
            <h3 className="font-semibold">Top Selling Items</h3>
          </div>
          <div className="h-80">
            <canvas ref={barChartRef} />
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <p className="text-gray-500 text-sm">Avg. Order Value</p>
          <p className="text-xl font-bold">৳420</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <p className="text-gray-500 text-sm">Busiest Hour</p>
          <p className="text-xl font-bold">1:00 PM - 2:00 PM</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <p className="text-gray-500 text-sm">Customer Retention</p>
          <p className="text-xl font-bold">68%</p>
        </div>
      </div>
    </div>
  );
}

export default SalesAnalytics;
